import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ComparePackagesTrayModule from './ComparePackageTrayModule';
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { useComparePkgTrayContext } from "./ComparePkgTrayContext/useComparePkgTrayContext";
import { fireTabHandleChange } from "@directv-digital/dds.services.analytics-service";
import { ComparePkgTray as ComparePkgTrayProps } from './ComparePackageTray.props';

export const ComparePackagesTray = (props: ComparePkgTrayProps) => {
  const { collapsedTray, trayOpen, updateCollapsedTray, setTrayOpen, selectedItems, triggerElementRef } = useComparePkgTrayContext();
  const collapsedTrayRef = React.useRef(collapsedTray);
  collapsedTrayRef.current = collapsedTray;
  const scrollHandledRef = React.useRef(false);
  const trayRef = React.useRef<HTMLDivElement>(null);
  // WAT-11106 — portal to document.body escapes any ancestor CSS containing block
  // (contentVisibility:auto or will-change:transform) that would otherwise trap
  // position:fixed relative to the component instead of the viewport.
  // No SSR guard needed: trayOpen is always false during SSR (set only by user
  // interaction), so createPortal/document.body are never reached server-side.
  // Generated by Copilot- Start - 2026-04-14 15:02:56
  // Ref keeps the latest selectedItems accessible inside the scroll handler
  // without adding selectedItems to the effect dependency array.
  // Adding the array reference as a dep would tear down and re-register the
  // 500ms timer on every chip select/unselect, creating a window where no
  // scroll listener is attached and potentially missing auto-collapse.
  const selectedItemsRef = React.useRef(selectedItems);
  selectedItemsRef.current = selectedItems;
  // Generated by Copilot- End - 2026-04-14 15:02:56

  // Auto-collapse tray on intentional scroll (ignore layout-shift jitter).
  // Re-registers each time the tray expands (collapsedTray becomes true).
  useEffect(() => {
    if (!trayOpen || !collapsedTray) {
      return;
    }
    trayRef.current?.focus({ preventScroll: true });
    scrollHandledRef.current = false;
    const initialScrollY = window.scrollY;
    const SCROLL_THRESHOLD = 30; // px — ignore small layout-shift scrolls

    // Generated by Copilot- Start - 2026-04-10 16:51:01
    // Declared before setTimeout so the callback closure can safely assign
    // cleanupRef.current without hitting the temporal dead zone (TDZ).
    const cleanupRef = { current: () => {} };
    // Generated by Copilot- End - 2026-04-10 16:51:01
    const activateTimer = setTimeout(() => {
      // Generated by Copilot- Start - 2026-04-10 16:51:01
      // cleanupRef is declared before setTimeout so the callback closure
      // can safely assign to it without hitting the temporal dead zone.
      // Generated by Copilot- End - 2026-04-10 16:51:01
      const handleScroll = () => {
        if (!collapsedTrayRef.current || scrollHandledRef.current) {
          return;
        }
        // Only auto-collapse for real user scrolls, not layout reflow
        if (Math.abs(window.scrollY - initialScrollY) < SCROLL_THRESHOLD) {
          return;
        }
        scrollHandledRef.current = true;
        fireTabHandleChange({
          event: "trackEvent",
          event_name: "link_click",
          linkName: "auto_collapse_tray",
          linkPosition: "comparison_tray",
          linkDestination: "comparison_tray"
        });
        if (selectedItemsRef.current.length === 0) {
          setTrayOpen(false);
          updateCollapsedTray(false);
        } else {
          updateCollapsedTray(false);
        }
        window.removeEventListener("scroll", handleScroll);
      };
      window.addEventListener("scroll", handleScroll, { passive: true });
      cleanupRef.current = () => window.removeEventListener("scroll", handleScroll);
    }, 500); // 500ms > 300ms CSS transition + buffer for slower devices
    return () => {
      clearTimeout(activateTimer);
      cleanupRef.current();
    };
  }, [trayOpen, collapsedTray]);

  // Click-outside-to-dismiss: replaces the old full-screen overlay (zIndex 1301)
  // that was blocking checkbox clicks on cards beneath it.
  // The checkbox wrapper in CardBottomSection already has stopPropagation,
  // so checkbox clicks won't reach this document handler.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!collapsedTrayRef.current) {
        return;
      }
      if (trayRef.current && !trayRef.current.contains(e.target as Node)) {
        updateCollapsedTray(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Generated by Copilot- Start - 2026-06-12 17:17:30
  // Focus trap: keep focus cycling within the tray when expanded
  const handleTrapKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab') {
      return;
    }
    const container = trayRef.current;
    if (!container) {
      return;
    }
    const focusable = container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // Attach/detach focus trap when tray is expanded
  useEffect(() => {
    if (trayOpen && collapsedTray) {
      document.addEventListener('keydown', handleTrapKeyDown);
      return () => document.removeEventListener('keydown', handleTrapKeyDown);
    }
  }, [trayOpen, collapsedTray, handleTrapKeyDown]);

  // Track previous collapsedTray to detect expanded→collapsed transition
  const prevCollapsedRef = React.useRef(collapsedTray);

  // Restore focus to the triggering element when tray closes or collapses
  useEffect(() => {
    const wasExpanded = prevCollapsedRef.current;
    prevCollapsedRef.current = collapsedTray;

    // Tray fully closed
    if (!trayOpen && triggerElementRef?.current) {
      triggerElementRef.current.focus();
      triggerElementRef.current = null;
      return;
    }
    // Tray collapsed from expanded state (expanded→collapsed)
    if (trayOpen && wasExpanded && !collapsedTray && triggerElementRef?.current) {
      triggerElementRef.current.focus();
      triggerElementRef.current = null;
    }
  }, [trayOpen, collapsedTray]);
  // Generated by Copilot- End - 2026-06-12 17:17:30

  try {
    if (!trayOpen) {
      return null;
    }
    return createPortal(
      <DtvBox ref={trayRef} id="comparepackagetray" tabIndex={-1}>
        <ComparePackagesTrayModule {...props} />
      </DtvBox>,
      document.body
    );
  } catch (error) {
    return <ErrorDisplay error={error} component="ComparePackagesTray Component error" />;
  }
}

